import {
  Button,
  Combobox,
  ErrorContainer,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Heading,
  IconBin,
  IconPrimaryButtonArrow,
  IconRefresh,
  ScrollArea,
} from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { SyncSongItem } from "./types/syncs.types";

import { useSyncsApplyForm } from "./hooks/useSyncsApplyForm";
import { useSyncsApplyMutation } from "./hooks/useSyncsApplyMutation";

function SyncsApply() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form,
    myAccount,
    userInfo,
    syncsData,
    isSuccess,
    noSyncs,
    pending,
    setPending,
    mutationErrorMessage,
    setMutationErrorMessage,
    options,
    cachedSongs,
    handleInputChange,
  } = useSyncsApplyForm();

  const { applySync } = useSyncsApplyMutation({
    form,
    cachedSongs,
    syncsData,
    myAccount,
    userInfo,
    setPending,
    setMutationErrorMessage,
  });

  useEffect(() => {
    if (noSyncs === true) {
      void navigate({ to: "/" });
    }
  }, [noSyncs, navigate]);

  return (
    <div className="flex-1 bg-sentric-rebrand-light-grey min-h-50 lg:min-h-screen-minus-header">
      <Heading as="h3" className="mb-8">{t("other:apply-for-sync", "Apply for a sync")}</Heading>
      <div className="bg-white shadow-sm rounded-lg p-4 md:px-14 md:py-10">

        {isSuccess && (
          <div className="w-full">
            <Form {...form}>
              <form
                className="flex flex-col gap-6"
                onSubmit={void form.handleSubmit(applySync)}
              >
                <div className="flex flex-col gap-2">
                  <Heading as="h4">{t("other:which-sync-header", "Which syncs are you applying for?")}</Heading>
                  <FormField
                    control={form.control}
                    name="opp"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Combobox
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder={t("other:select-sync-placeholder", "Select sync")}
                            searchByLabel
                            data={syncsData.map(sync => ({
                              value: sync._id,
                              label: sync.name,
                            }))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Heading as="h4">{t("other:select-one-song-header", "Select at least 1 song")}</Heading>
                  <FormField
                    control={form.control}
                    name="songList"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Combobox
                            customWidth
                            triggerClassName="w-full max-w-none"
                            multiSelectDisplay="count"
                            placeholder={t("other:select-a-song-placeholder", "Select a song")}
                            values={field.value.map((s: SyncSongItem) => s.workcode)}
                            onValuesChange={(workcodes) => {
                              field.onChange(
                                workcodes.map((wc) => {
                                  const existing = field.value.find(
                                    (s: SyncSongItem) => s.workcode === wc,
                                  );
                                  if (existing !== undefined)
                                    return existing;
                                  const cached = cachedSongs.find(s => s.workcode === wc);
                                  return {
                                    workcode: wc,
                                    title: cached?.title ?? "",
                                    performingartists: cached?.performingartists ?? "",
                                    label: cached !== undefined
                                      ? `${cached.title} - (${cached.performingartists})`
                                      : wc,
                                  };
                                }),
                              );
                            }}
                            data={options}
                            onSearchChange={handleInputChange}
                          />
                        </FormControl>
                        {field.value.length > 0 && (
                          <ScrollArea className="mt-2 h-48 rounded-lg border border-sentric-mid-grey">
                            <div className="flex flex-col gap-2 p-1">
                              {field.value.map((song: SyncSongItem) => (
                                <div
                                  key={song.workcode}
                                  className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-sm"
                                >
                                  <span className="font-semibold">{song.label ?? song.title}</span>
                                  <Button
                                    type="button"
                                    variant="link"
                                    aria-label={t("common:remove", "Remove")}
                                    icon={(
                                      <IconBin className="w-5 h-5 stroke-black hover:stroke-red-600" />
                                    )}
                                    onClick={() => {
                                      field.onChange(
                                        field.value.filter(
                                          (s: SyncSongItem) => s.workcode !== song.workcode,
                                        ),
                                      );
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Heading as="h4">{t("other:tell-us-more-header", "Tell us more about you")}</Heading>
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <textarea
                            id="txtSyncEvidence"
                            placeholder={t("other:supporting-text-placeholder", "Supporting text")}
                            rows={4}
                            className="w-full font-sentric-alt font-medium text-sm rounded-lg px-2 py-1.5 ring-1 ring-sentric-dark-background hover:ring-2 hover:ring-sentric-orange focus-visible:ring-2 focus-visible:ring-sentric-orange focus-visible:outline-none placeholder:text-black placeholder:text-opacity-50 transition ease-out duration-200 resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-3">
                  {mutationErrorMessage.length > 0 && (
                    <ErrorContainer message={mutationErrorMessage} />
                  )}
                  {form.formState.errors.root?.message != null && (
                    <ErrorContainer message={form.formState.errors.root.message} />
                  )}
                  <div className="flex justify-end">
                    <Button
                      id="btnSyncSubmit"
                      type="submit"
                      variant="primary"
                      disabled={pending}
                      label={t("common:submit-button", "Submit")}
                      icon={
                        pending
                          ? <IconRefresh className="animate-spin" />
                          : <IconPrimaryButtonArrow />
                      }
                    />
                  </div>
                </div>
              </form>
            </Form>
          </div>
        )}
      </div>
    </div>
  );
}

export default SyncsApply;
